/**
 * ExportPanel — Inline popover replacement for ExportModal.
 *
 * Same backend contract (POST /api/v1/admin/leads/export with {format, fields[], filters})
 * but rendered as a popover anchored to the Export button. Zero modal scrim.
 *
 * Three tiers (Essential / Business / Full) with per-field chip toggle, format
 * selector (CSV / XLSX), and a live "X fields selected" badge.
 */
import { useEffect, useState } from 'react';
import { Download, ChevronDown, ChevronUp } from 'lucide-react';
import { Btn } from '../ui/primitives';

const TIERS = {
  essential: {
    label: 'Essential',
    desc: 'Name, email, phone, status — quick share',
    fields: ['first_name', 'last_name', 'email', 'phone', 'business_name', 'status', 'created_at'],
  },
  business: {
    label: 'Business',
    desc: '+ industry, revenue, services, scoring',
    fields: ['first_name', 'last_name', 'email', 'phone', 'business_name', 'location', 'industry', 'annual_revenue', 'services', 'timeline', 'status', 'created_at', 'audience', 'entity_type', 'accounting_software', 'grade', 'score', 'assigned_to'],
  },
  full: {
    label: 'Full',
    desc: 'Every field including IP / spam / system metadata',
    fields: null, // null = all fields
  },
};

const FIELD_LABEL = {
  first_name: 'First name', last_name: 'Last name', email: 'Email', phone: 'Phone',
  business_name: 'Business name', location: 'Location', industry: 'Industry',
  annual_revenue: 'Annual revenue', services: 'Services', timeline: 'Timeline',
  story: 'Story', status: 'Status', created_at: 'Created at', email_verified: 'Email verified',
  audience: 'Audience', entity_type: 'Entity type', accounting_software: 'Accounting software',
  states: 'States', revenue_location: 'Revenue location', referral_source: 'Referral source',
  referrer_name: 'Referrer name', grade: 'Grade', score: 'Score',
  assigned_to: 'Assigned to', assigned_by: 'Assigned by', assigned_at: 'Assigned at',
  ip_address: 'IP', country: 'Country', city: 'City',
};

export default function ExportPanel({ onClose, onExport, isLoading, totalCount = 0 }) {
  const [tier, setTier] = useState('business');
  const [fields, setFields] = useState(TIERS.business.fields);
  const [format, setFormat] = useState('csv');
  const [showFields, setShowFields] = useState(false);

  useEffect(() => {
    if (tier === 'full') {
      setFields(null);
    } else {
      setFields(TIERS[tier].fields);
    }
  }, [tier]);

  const toggleField = (f) => {
    if (!fields) return;
    setFields((prev) => prev.includes(f) ? (prev || []).filter((x) => x !== f) : [...prev, f]);
  };

  const handleExport = () => {
    onExport({ format, fields: fields || 'all', exportType: tier });
  };

  const fieldCount = fields ? fields.length : 'All';

  return (
    <div
      data-testid="export-panel"
      style={{ minWidth: 360, maxWidth: 420 }}
    >
      <h4 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 800, color: 'var(--text)' }}>
        Export {totalCount > 0 ? `${totalCount.toLocaleString()} leads` : 'leads'}
      </h4>

      <div style={{ marginBottom: 14 }}>
        <span className="lb-lbl">Field tier</span>
        <div style={{ display: 'grid', gap: 6 }}>
          {Object.entries(TIERS).map(([k, t]) => (
            <button
              key={k}
              data-testid={`export-tier-${k}`}
              onClick={() => setTier(k)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '10px 12px', borderRadius: 8,
                border: `1.5px solid ${tier === k ? 'var(--accent)' : 'var(--border)'}`,
                background: tier === k ? 'var(--info-soft)' : 'var(--surface-2)',
                cursor: 'pointer', textAlign: 'left',
                fontFamily: 'inherit', color: 'inherit',
              }}
            >
              <span style={{
                width: 16, height: 16, borderRadius: 99, marginTop: 2,
                border: `2px solid ${tier === k ? 'var(--accent)' : 'var(--field-border)'}`,
                background: tier === k ? 'var(--accent)' : 'transparent',
                boxShadow: tier === k ? 'inset 0 0 0 3px var(--surface)' : 'none',
                flex: '0 0 16px',
              }} />
              <span style={{ flex: 1 }}>
                <b style={{ display: 'block', fontSize: 13.5, color: 'var(--text)', marginBottom: 2 }}>{t.label}</b>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{t.desc}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {fields && fields.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <button
            data-testid="export-fields-toggle"
            onClick={() => setShowFields((s) => !s)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              border: 'none', background: 'transparent',
              padding: 0, fontSize: 12, fontWeight: 700, color: 'var(--accent-deep)',
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {showFields ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            {showFields ? 'Hide' : 'Customise'} fields ({fieldCount} selected)
          </button>
          {showFields && (
            <div data-testid="export-fields-grid" style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 180, overflowY: 'auto', padding: '6px 2px' }}>
              {TIERS.business.fields.concat(['audience', 'entity_type', 'accounting_software', 'grade', 'score', 'ip_address', 'country', 'city']).filter((f, i, a) => a.indexOf(f) === i).map((f) => {
                const on = fields.includes(f);
                return (
                  <button
                    key={f}
                    onClick={() => toggleField(f)}
                    data-testid={`export-field-${f}`}
                    style={{
                      padding: '4px 9px', borderRadius: 99,
                      border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                      background: on ? 'var(--accent)' : 'var(--surface-2)',
                      color: on ? '#fff' : 'var(--text-2)',
                      fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {FIELD_LABEL[f] || f}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <span className="lb-lbl">Format</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {['csv', 'xlsx'].map((fmt) => (
            <button
              key={fmt}
              data-testid={`export-format-${fmt}`}
              onClick={() => setFormat(fmt)}
              style={{
                flex: 1, padding: '7px 10px', borderRadius: 8,
                border: `1.5px solid ${format === fmt ? 'var(--accent)' : 'var(--border)'}`,
                background: format === fmt ? 'var(--info-soft)' : 'var(--surface-2)',
                color: format === fmt ? 'var(--accent-deep)' : 'var(--text-2)',
                fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                fontFamily: 'inherit', textTransform: 'uppercase',
              }}
            >
              {fmt}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <Btn data-testid="export-panel-btn-2" variant="ghost" sm onClick={onClose} testid="export-cancel">Cancel</Btn>
        <Btn data-testid="export-panel-btn-1" variant="primary" sm icon={Download} onClick={handleExport} disabled={isLoading} testid="export-confirm">
          {isLoading ? 'Exporting…' : 'Export'}
        </Btn>
      </div>
    </div>
  );
}
